import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Steps, message } from "antd";
import axios from "axios";
import { AddTalentProfileWrapper } from "./AddTalentProfile.style";
import BasicInformation from "./BasicInformation";
import AdditionalInformation from "./AdditionalInformation";
import { API_CONST } from "../../../../const";

const { Step } = Steps;

const AddTalentProfile = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const isEditMode = Boolean(editId);
  
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [partnerOrganizations, setPartnerOrganizations] = useState([]);
  const [formData, setFormData] = useState({
    partnerOrganization: "",
    name: "",
    role: "",
    primarySkills: [],
    secondarySkills: [],
    skills: [],
    experienceYears: "",
    experienceMonths: "",
    rate: "",
    notice: "",
    email: "",
    phone: "",
    location: "",
    availability: "",
    summary: "",
    projects: [],
    resume: null,
    aadhaar: null,
    pan: null,
    degree: null,
  });

  useEffect(() => {
    fetchPartnerOrganizations();
    if (isEditMode && editId) {
      fetchTalentProfile(editId);
    }
  }, [isEditMode, editId]);

  const fetchPartnerOrganizations = async () => {
    try {
      const response = await axios.get(API_CONST.GET_PARTNER_ORGANIZATIONS);
      setPartnerOrganizations(response.data.organizations || []);
    } catch (error) {
      console.error("Error fetching partner organizations:", error);
      message.error("Failed to load partner organizations");
    }
  };

  const fetchTalentProfile = async (id) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.get(`${API_CONST.GET_TALENT_PROFILE}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.profile) {
        setFormData(response.data.profile);
      }
    } catch (error) {
      console.error("Error fetching talent profile:", error);
      message.error("Failed to load talent profile");
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      title: "Basic Information",
      component: BasicInformation,
    },
    {
      title: "Additional Information",
      component: AdditionalInformation,
    },
  ];

  const handleNext = (data) => {
    setFormData({ ...formData, ...data });
    setCurrentStep(currentStep + 1);
  };

  const handleBack = (data = {}) => {
    setFormData({ ...formData, ...data });
    setCurrentStep(currentStep - 1);
  };

  const handleCancel = () => {
    navigate("/admin/talent-profiles");
  };

  const handleSubmit = async (data) => {
    const finalData = { ...formData, ...data };
    
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const headers = { Authorization: `Bearer ${token}` };

      const primarySkillsFiltered = (finalData.primarySkills || []).filter(s => s);
      
      const payload = {
        partnerOrganization: finalData.partnerOrganization,
        name: finalData.name,
        role: finalData.role,
        primarySkills: primarySkillsFiltered,
        secondarySkills: finalData.secondarySkills || [],
        skills: finalData.skills || [],
        experienceYears: parseInt(finalData.experienceYears) || 0,
        experienceMonths: parseInt(finalData.experienceMonths) || 0,
        rate: parseFloat(finalData.rate) || 0,
        notice: finalData.notice,
        email: finalData.email,
        phone: finalData.phone,
        location: finalData.location,
        availability: finalData.availability,
        summary: finalData.summary || "",
        projects: finalData.projects || [],
      };

      if (finalData.resumeUrl) payload.resumeUrl = finalData.resumeUrl;
      if (finalData.aadhaarUrl) payload.aadhaarUrl = finalData.aadhaarUrl;
      if (finalData.panUrl) payload.panUrl = finalData.panUrl;
      if (finalData.degreeUrl) payload.degreeUrl = finalData.degreeUrl;

      if (isEditMode) {
        await axios.put(`${API_CONST.UPDATE_TALENT_PROFILE}/${editId}`, payload, { headers });
        message.success("Talent profile updated successfully!");
      } else {
        await axios.post(API_CONST.ADD_TALENT_PROFILE, payload, { headers });
        message.success("Talent profile created successfully!");
      }
      navigate("/admin/talent-profiles");
    } catch (error) {
      console.error("Error saving talent profile:", error);
      message.error(error.response?.data?.message || "Failed to save talent profile");
    } finally {
      setLoading(false);
    }
  };

  const CurrentStepComponent = steps[currentStep].component;

  return (
    <AddTalentProfileWrapper>
      <div className="page-header">
        <h1>{isEditMode ? "Edit Talent Profile" : "Add New Talent Profile"}</h1>
        {isEditMode && (
          <span style={{ color: "#666", fontSize: 14, marginLeft: 12 }}>
            Editing: {formData.name}
          </span>
        )}
      </div>

      <div className="form-container">
        <div className="steps-container">
          <Steps current={currentStep}>
            {steps.map((step, index) => (
              <Step key={index} title={step.title} />
            ))}
          </Steps>
        </div>

        <div className="form-card">
          <CurrentStepComponent
            initialData={formData}
            onNext={handleNext}
            onBack={handleBack}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isFirstStep={currentStep === 0}
            isLastStep={currentStep === steps.length - 1}
            isEditMode={isEditMode}
            partnerOrganizations={partnerOrganizations}
            loading={loading}
          />
        </div>
      </div>
    </AddTalentProfileWrapper>
  );
};

export default AddTalentProfile;
